import { useEffect, useState } from 'react'
import Lenis from 'lenis'
import Planet from '@/components/Planet'
import DemoForm from '@/components/DemoForm'
import ResourceModal from '@/components/ResourceModal'

/* Inline icons — themeable, dependency-free. */
const Icon = {
  arrow: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
    </svg>
  ),
  spreadsheet: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 10h18M9 4v16M15 4v16"/>
    </svg>
  ),
  clock: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>
    </svg>
  ),
  fragmented: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/>
      <rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/>
    </svg>
  ),
  scale: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 20h18"/><path d="M6 20V9"/><path d="M12 20V4"/><path d="M18 20v-7"/>
    </svg>
  ),
  shield: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" /><path d="m9 12 2 2 4-4" />
    </svg>
  ),
  audit: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z"/><path d="M14 2v6h6"/><path d="m10 15 2 2 4-4"/>
    </svg>
  ),
  bolt: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8Z"/>
    </svg>
  ),
  strategy: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20v-6"/><path d="M12 14a4 4 0 1 0-4-4"/><path d="M20 4h-4v4"/><path d="m14 10 6-6"/>
    </svg>
  ),
  chart: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19V5"/><path d="M4 19h16"/><path d="M8 16v-4"/><path d="M13 16V8"/><path d="M18 16v-7"/>
    </svg>
  ),
  linkedin: (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.451 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.355V9h3.414v1.561h.049c.476-.9 1.637-1.85 3.37-1.85 3.602 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 1 1 .001-4.125 2.062 2.062 0 0 1-.001 4.125zM7.114 20.452H3.558V9h3.556v11.452z"/>
    </svg>
  ),
}

const rd = (ms) => ({ '--rd': `${ms}ms` })

const NAV = [
  { label: 'Platform', href: '#platform' },
  { label: 'Modules', href: '#modules' },
  { label: 'Customers', href: '#customers' },
  { label: 'Resources', href: '#resources' },
  { label: 'FAQ', href: '#faq' },
]

const TRUST_STATS = [
  { value: '100+', label: 'Organisations trust QC4' },
  { value: '100%', label: 'Controls tested, not samples' },
  { value: '10 yrs', label: 'Since 2015 in the market' },
  { value: '∞', label: 'Unlimited user licensing' },
]

const PROBLEM = [
  { icon: Icon.spreadsheet, title: 'Sample-based blind spots', body: 'Testing 3% of transactions leaves 97% of your exposure invisible until it hits the regulator.' },
  { icon: Icon.clock, title: 'Painful reporting cycles', body: 'Weeks of manual evidence gathering just to answer questions your data already knows the answer to.' },
  { icon: Icon.fragmented, title: 'No single source of truth', body: 'Risks in spreadsheets. Incidents in email. Controls in another tool. Nothing reconciles.' },
  { icon: Icon.scale, title: 'Assurance that can\'t scale', body: 'Every new product, region or regulation multiplies the manual work — your team can\'t keep up.' },
]

const MODULES = [
  { n: '01', title: 'Risk Management', body: 'Dynamic risk registers, assessments and real-time monitoring across your enterprise.' },
  { n: '02', title: 'Incident Management', body: 'Fast reporting, structured investigation and automated escalation workflows.' },
  { n: '03', title: 'Enterprise Content Management', body: 'Policies, procedures and controlled document version history — with attestation.' },
  { n: '04', title: 'Internal Audit', body: 'Risk-based planning through to reporting, powered by live control evidence.' },
  { n: '05', title: 'Business Continuity', body: 'BCP planning, testing and dependency mapping for genuine operational resilience.' },
  { n: '06', title: 'Asset Management', body: 'Critical asset registers, ownership, criticality and lifecycle oversight.' },
  { n: '07', title: 'Regulatory Engagements', body: 'Track obligations, responses and correspondence in one auditable place.' },
  { n: '08', title: 'Change Management', body: 'Controlled business and IT change, with pre-implementation risk sign-off.' },
  { n: '09', title: 'Third-Party & Credentials', body: 'Vendor risk, contracts, credentials and expiries — never miss a renewal.' },
  { n: '10', title: 'Business Improvement', body: 'Continuous improvement loops that convert findings into measurable outcomes.' },
]

const OUTCOMES = [
  { icon: Icon.shield, title: 'Reduce risk exposure', body: 'Operational and conduct risks caught population-wide, in real time.' },
  { icon: Icon.clock, title: 'Cut compliance time', body: 'Days instead of weeks on audit prep, regulator responses and board packs.' },
  { icon: Icon.audit, title: 'Regulator-ready reporting', body: 'Evidence and lineage at your fingertips — every metric traceable.' },
  { icon: Icon.strategy, title: 'Free your team for strategy', body: 'Shift from spreadsheet plumbing to genuinely strategic risk work.' },
  { icon: Icon.chart, title: 'Scale as you grow', body: 'Unlimited users, unlimited entities — obligations scale, cost doesn\'t.' },
]

/* Testimonials from tmrglobal.com, first names + role/industry only. */
const TESTIMONIALS = [
  { tag: 'CENTRALISE', name: 'Neil', role: 'Chief Risk Officer', industry: 'Technology',
    quote: 'With QC4, we finally have a single source of truth for all our risk and compliance data. The centralised dashboard provides unparalleled visibility into our risk posture.' },
  { tag: 'AUTOMATE', name: 'Chen', role: 'Compliance Manager', industry: 'Financial Services',
    quote: 'QC4 has completely transformed our control testing process. What used to take weeks now takes days. The automation is a game-changer.' },
  { tag: 'ESCALATE', name: 'Garth', role: 'Head of Operational Risk', industry: 'Manufacturing',
    quote: 'We\'ve reduced our issue escalation time by over 50% since implementing QC4. The streamlined workflow and automated notifications made a huge difference.' },
  { tag: 'SIMPLIFY', name: 'Wing', role: 'Training Manager', industry: 'Insurance',
    quote: 'The user interface is intuitive and easy to navigate. Even our non-technical users were able to quickly learn how to use QC4.' },
  { tag: 'REPORT', name: 'Geoff', role: 'Compliance Officer', industry: 'Energy',
    quote: 'The real-time reporting in QC4 is invaluable. We can quickly generate reports for management and regulators, demonstrating our commitment to compliance.' },
  { tag: 'CONTROL', name: 'Mark', role: 'President', industry: 'Australian Building Company',
    quote: 'QC4 has helped us build a stronger, more resilient organisation. It\'s given us the confidence to take on new challenges knowing our risk is under control.' },
]

const RESOURCES = [
  {
    slug: 'population-level-controls-testing',
    kind: 'Whitepaper',
    title: 'From sample-based to population-level controls testing',
    body: 'A practical guide for risk and compliance leaders on moving beyond spreadsheets and 3% samples — with a phased 90-day plan.',
    cta: 'Download the guide',
  },
  {
    slug: 'audit-prep-case-study',
    kind: 'Case Study',
    title: 'How a mid-market financial services firm cut audit prep by 70%',
    body: 'The playbook one CRO used to consolidate 14 spreadsheets, three GRC point-tools and 400+ hours of manual work into QC4.',
    cta: 'Read the case study',
  },
  {
    slug: 'grc-assessment-checklist',
    kind: 'Buyer\'s Kit',
    title: 'The GRC platform buyer\'s checklist for regulated industries',
    body: 'The 24 questions that separate a modern platform from a rebranded document repository — with a scoring matrix.',
    cta: 'Get the checklist',
  },
]

const FAQS = [
  { q: 'How is QC4 different from other GRC platforms?',
    a: 'Most GRC tools are document repositories with dashboards on top. QC4 was built to actively test controls across your entire population, not samples — so failure notifications are automatic and evidence is a by-product of running the business, not a project.' },
  { q: 'How long does implementation take?',
    a: 'Most customers are live on their first module within weeks, not quarters. The modular architecture means you deploy what you need first (typically Risk + Frontline Assurance) and add modules as adoption grows.' },
  { q: 'Do you charge per user?',
    a: 'No. QC4 comes with unlimited user licensing, so you can involve first-line, second-line and audit teams without every seat becoming a budget conversation.' },
  { q: 'Can QC4 ingest data from our core systems?',
    a: 'Yes. QC4 accepts configurable templates and direct ingestion from your core banking, ERP, ticketing and identity systems via API — the same connectors that power population-level controls testing.' },
  { q: 'Is QC4 suitable for financial services and regulated industries?',
    a: 'It\'s the primary use case. QC4 is deployed across financial services, insurance, healthcare, energy, manufacturing and government — anywhere assurance, incident management and regulator engagement have to stand up to scrutiny.' },
  { q: 'What about data residency and security?',
    a: 'QC4 is deployed with enterprise-grade security controls and can be hosted in-region for organisations with data residency obligations. A specialist can walk you through architecture and controls in the demo.' },
]

export default function Home() {
  const [openFaq, setOpenFaq] = useState(0)
  const [activeResource, setActiveResource] = useState(null)

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    let lenis, raf
    if (!reduce) {
      lenis = new Lenis({ duration: 1.15, smoothWheel: true, touchMultiplier: 1.5 })
      const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop) }
      raf = requestAnimationFrame(loop)
    }

    const els = document.querySelectorAll('[data-reveal]')
    if (reduce) {
      els.forEach((el) => el.classList.add('in'))
      return () => {}
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target) }
        })
      },
      { threshold: 0.15, rootMargin: '0px 0px -6% 0px' },
    )
    els.forEach((el) => io.observe(el))

    return () => { if (raf) cancelAnimationFrame(raf); lenis?.destroy(); io.disconnect() }
  }, [])

  return (
    <div className="qc-app" id="top">
      <Planet />

      {/* NAV */}
      <nav className="qc-nav" aria-label="Primary">
        <div className="qc-nav-inner">
          <a className="qc-brand" href="#top" data-testid="nav-brand">
            <img src="/tmr-global-logo.png" alt="TMR Global" className="qc-brand-logo" />
          </a>
          <ul className="qc-nav-links">
            {NAV.map((n) => (
              <li key={n.label}><a href={n.href} data-testid={`nav-${n.label.toLowerCase()}`}>{n.label}</a></li>
            ))}
          </ul>
          <a className="qc-btn qc-btn-ghost" href="#demo" data-testid="nav-cta">Book demo</a>
        </div>
      </nav>

      {/* HERO */}
      <header className="qc-hero" id="platform">
        <div className="qc-hero-inner">
          <span className="qc-pill" data-reveal style={rd(0)}>
            <span className="qc-pill-dot" /> Trusted by 100+ regulated organisations · since 2015
          </span>
          <h1 className="qc-hero-title" data-reveal style={rd(80)} data-testid="hero-title">
            Stop managing risk like it&apos;s <em>2010</em>.
          </h1>
          <p className="qc-hero-sub" data-reveal style={rd(180)}>
            QC4 gives you total risk control in one unified platform. Its modular, AI‑powered design centralises
            frontline assurance, automates population‑level controls testing, and delivers real‑time
            intelligence — enabling organisations to cut exposure, prove compliance, and operate with certainty.
          </p>
          <div className="qc-hero-actions" data-reveal style={rd(300)}>
            <a className="qc-btn qc-btn-primary" href="#demo" data-testid="hero-cta-primary">
              Book Demo {Icon.arrow}
            </a>
            <a className="qc-btn qc-btn-outline" href="#customers" data-testid="hero-cta-secondary">
              Client Feedback
            </a>
          </div>
          <div className="qc-trust-line" data-reveal style={rd(420)}>
            Unlimited user licensing · Fast implementation · <strong>Population-level</strong> controls testing
          </div>
        </div>

        <div className="qc-trustbar" data-reveal style={rd(560)}>
          {TRUST_STATS.map((s, i) => (
            <div className="qc-stat" key={s.label} data-reveal style={rd(i * 80)}>
              <span className="qc-stat-value">{s.value}</span>
              <span className="qc-stat-label">{s.label}</span>
            </div>
          ))}
        </div>
      </header>

      {/* PROBLEM */}
      <section className="qc-section" id="problem">
        <div className="qc-section-head">
          <span className="qc-eyebrow" data-reveal>The old way is breaking</span>
          <h2 data-reveal style={rd(80)}>Legacy tools are holding your business back</h2>
          <p data-reveal style={rd(160)}>
            Spreadsheets, disconnected systems and manual processes create dangerous blind spots.
            In today&apos;s regulatory environment, this approach is no longer acceptable — or sustainable.
          </p>
        </div>
        <div className="qc-problem-grid">
          {PROBLEM.map((p, i) => (
            <article className="qc-card" key={p.title} data-reveal style={rd(i * 90)} data-testid={`problem-${i}`}>
              <span className="qc-card-icon">{p.icon}</span>
              <h3>{p.title}</h3>
              <p>{p.body}</p>
            </article>
          ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: 44, ...rd(380) }} data-reveal>
          <a className="qc-btn qc-btn-primary" href="#demo" data-testid="problem-cta">It&apos;s time for a modern approach — Book a demo {Icon.arrow}</a>
        </div>
      </section>

      {/* DIFFERENTIATOR */}
      <section className="qc-section" id="differentiator">
        <div className="qc-showcase-grid">
          <div className="qc-showcase-copy">
            <span className="qc-eyebrow" data-reveal>The QC4 difference</span>
            <h2 data-reveal style={rd(80)}>
              Centralised frontline assurance at true <em style={{color:'var(--accent)', fontStyle:'normal'}}>enterprise scale</em>.
            </h2>
            <p data-reveal style={rd(160)}>
              QC4&apos;s integrated centralised frontline assurance module lets you collect data your way —
              through configurable templates or direct ingestion from your core systems.
            </p>
            <p data-reveal style={rd(220)}>
              Once centralised, QC4 automatically tests controls across your entire population — not samples —
              delivering immediate failure notifications and real-time risk visibility at speed and scale.
            </p>
            <ul className="qc-benefit-list" data-reveal style={rd(280)}>
              <li>Stronger operational risk and conduct risk oversight</li>
              <li>Dramatically faster audit and regulatory reporting</li>
              <li>Proactive mitigation of emerging risks</li>
              <li>Significant reduction in manual compliance workload</li>
            </ul>
            <a className="qc-btn qc-btn-primary" href="#demo" data-reveal style={rd(340)} data-testid="diff-cta">
              Experience population-level assurance {Icon.arrow}
            </a>
          </div>

          <div className="qc-dashboard" data-reveal style={rd(200)} aria-hidden="true">
            <div className="qc-dash-top">
              <span className="qc-dash-title">Controls · Population-level assurance</span>
              <span className="qc-dash-badge"><span className="qc-pill-dot" /> Live</span>
            </div>
            <div className="qc-dash-bars">
              {[62, 88, 47, 95, 71, 80, 58].map((h, i) => (
                <span key={i} className="qc-dash-bar" style={{ '--h': `${h}%`, '--d': `${i * 90}ms` }} />
              ))}
            </div>
            <div className="qc-dash-rows">
              {[
                ['Transactions tested', '100%', '2.4M'],
                ['Control failures caught', '98.7%', '312'],
                ['Time to notify', '< 5m', 'live'],
                ['Audit-ready evidence', '100%', 'auto'],
              ].map(([label, w, val]) => (
                <div className="qc-dash-row" key={label}>
                  <span>{label}</span>
                  <span className="qc-dash-track"><span className="qc-dash-fill" style={{ '--w': w }} /></span>
                  <span className="qc-dash-val">{val}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MODULES */}
      <section className="qc-section" id="modules">
        <div className="qc-section-head">
          <span className="qc-eyebrow" data-reveal>The platform</span>
          <h2 data-reveal style={rd(80)}>Modular GRC. Built for complexity.</h2>
          <p data-reveal style={rd(160)}>
            Everything you need to govern, assure and improve — seamlessly connected in one AI-powered platform.
            Turn on what you need first; the rest is there when you&apos;re ready.
          </p>
        </div>
        <div className="qc-modules-grid">
          {MODULES.map((m, i) => (
            <article className="qc-module" key={m.title} data-reveal style={rd((i % 3) * 80 + Math.floor(i/3) * 60)} data-testid={`module-${i}`}>
              <span className="qc-module-num">{m.n}</span>
              <h3>{m.title}</h3>
              <p>{m.body}</p>
            </article>
          ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: 48 }} data-reveal>
          <a className="qc-btn qc-btn-primary" href="#demo" data-testid="modules-cta">See the full platform in action {Icon.arrow}</a>
        </div>
      </section>

      {/* OUTCOMES */}
      <section className="qc-section" id="outcomes">
        <div className="qc-section-head">
          <span className="qc-eyebrow" data-reveal>Outcomes</span>
          <h2 data-reveal style={rd(80)}>Real control. Real results.</h2>
          <p data-reveal style={rd(160)}>
            The measurable shift customers see after moving from legacy GRC to QC4.
          </p>
        </div>
        <div className="qc-outcome-grid">
          {OUTCOMES.slice(0, 4).map((o, i) => (
            <article className="qc-card" key={o.title} data-reveal style={rd(i * 90)} data-testid={`outcome-${i}`}>
              <span className="qc-card-icon">{o.icon}</span>
              <h3>{o.title}</h3>
              <p>{o.body}</p>
            </article>
          ))}
        </div>
        <div className="qc-outcome-grid" style={{ marginTop: 18, gridTemplateColumns: '1fr' }}>
          <article className="qc-card" data-reveal>
            <span className="qc-card-icon">{OUTCOMES[4].icon}</span>
            <h3>{OUTCOMES[4].title}</h3>
            <p>{OUTCOMES[4].body}</p>
          </article>
        </div>
      </section>

      {/* CUSTOMERS / TESTIMONIALS */}
      <section className="qc-section" id="customers">
        <div className="qc-section-head">
          <span className="qc-eyebrow" data-reveal>What customers say</span>
          <h2 data-reveal style={rd(80)}>The proof is in the workflow</h2>
          <p data-reveal style={rd(160)}>
            Risk, audit and compliance leaders across financial services, insurance, energy, manufacturing and technology
            share what changed once QC4 replaced their spreadsheets.
          </p>
        </div>
        <div className="qc-testi-grid">
          {TESTIMONIALS.map((t, i) => (
            <article className="qc-testi" key={t.name + t.role} data-reveal style={rd(i * 80)} data-testid={`testi-${i}`}>
              <span className="qc-testi-tag">We {t.tag} for you</span>
              <p className="qc-testi-quote">{t.quote}</p>
              <div className="qc-testi-meta">
                <span className="qc-testi-avatar">{t.name.slice(0,1)}</span>
                <div className="qc-testi-who">
                  <strong>{t.name}</strong>
                  {t.role} · {t.industry}
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* RESOURCES */}
      <section className="qc-section" id="resources">
        <div className="qc-section-head">
          <span className="qc-eyebrow" data-reveal>Resources</span>
          <h2 data-reveal style={rd(80)}>Sharpen your GRC playbook</h2>
          <p data-reveal style={rd(160)}>
            Practical whitepapers, playbooks and buyer&apos;s kits from the TMR Global team — free to download.
          </p>
        </div>
        <div className="qc-resources-grid">
          {RESOURCES.map((r, i) => (
            <article className="qc-resource" key={r.title} data-reveal style={rd(i * 90)} data-testid={`resource-${i}`}>
              <span className="qc-resource-kind">{r.kind}</span>
              <h3>{r.title}</h3>
              <p>{r.body}</p>
              <button
                type="button"
                className="qc-resource-cta"
                onClick={() => setActiveResource(r)}
                data-testid={`resource-${i}-cta`}
              >
                {r.cta} {Icon.arrow}
              </button>
            </article>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="qc-section" id="faq">
        <div className="qc-section-head">
          <span className="qc-eyebrow" data-reveal>Frequently asked</span>
          <h2 data-reveal style={rd(80)}>The GRC questions we hear most</h2>
        </div>
        <div className="qc-faq">
          {FAQS.map((item, i) => (
            <div
              className="qc-faq-item"
              data-open={openFaq === i ? 'true' : 'false'}
              key={item.q}
              data-reveal
              style={rd(i * 60)}
              data-testid={`faq-item-${i}`}
            >
              <button
                type="button"
                className="qc-faq-q"
                aria-expanded={openFaq === i}
                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                data-testid={`faq-q-${i}`}
              >
                <span>{item.q}</span>
                <span className="qc-plus" aria-hidden="true">+</span>
              </button>
              <div className="qc-faq-a" data-testid={`faq-a-${i}`}>{item.a}</div>
            </div>
          ))}
        </div>
      </section>

      {/* DEMO */}
      <section className="qc-section" id="demo">
        <div className="qc-section-head left">
          <span className="qc-eyebrow" data-reveal>Book your demo</span>
          <h2 data-reveal style={rd(80)}>See QC4 running on real risk data — in 30 minutes.</h2>
          <p data-reveal style={rd(160)}>
            A QC4 specialist will walk you through the platform, tailor the demo to your risk framework,
            and show you exactly how population-level controls testing replaces the spreadsheet.
          </p>
        </div>
        <div className="qc-demo-grid">
          <div className="qc-demo-copy" data-reveal>
            <span className="qc-eyebrow">What to expect</span>
            <h2>A working session, not a sales pitch.</h2>
            <ul className="qc-benefit-list qc-demo-bullets">
              <li>Tailored to financial services and regulated industries</li>
              <li>Live population-level controls testing walkthrough</li>
              <li>Q&amp;A with a QC4 specialist — bring your risk team</li>
              <li>Straight-talk pricing (unlimited user licensing)</li>
            </ul>
            <div className="qc-demo-tidy">
              <h4>Prefer to pick a slot yourself?</h4>
              <p>Grab a time directly on Daniel Benson&apos;s calendar — no forms, no back-and-forth.</p>
              <a
                href="https://tidycal.com/dbenson/60-minute-meeting"
                target="_blank" rel="noopener noreferrer"
                className="qc-btn qc-btn-outline"
                data-testid="tidycal-link"
              >
                Open TidyCal booking {Icon.arrow}
              </a>
            </div>
          </div>
          <div data-reveal style={rd(180)}>
            <DemoForm />
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="qc-footer-wrap">
        <div className="qc-footer">
          <div className="qc-footer-brand">
            <div className="qc-footer-brand-row">
              <a className="qc-brand" href="#top">
                <img src="/tmr-global-logo.png" alt="TMR Global" className="qc-brand-logo" />
                <span className="qc-brand-text"><strong>QC4</strong> · GRC Platform</span>
              </a>
              <a
                href="https://www.linkedin.com/company/tmrglobal/?viewAsMember=true"
                target="_blank" rel="noopener noreferrer"
                className="qc-linkedin-icon"
                aria-label="Follow TMR Global on LinkedIn"
                data-testid="footer-linkedin-icon"
              >
                {Icon.linkedin}
              </a>
            </div>
            <p>
              Simplifying Governance, Risk and Compliance for regulated organisations worldwide.
              Track, manage and report — seamlessly, securely and globally.
            </p>
            <a
              href="https://www.linkedin.com/company/tmrglobal/?viewAsMember=true"
              target="_blank" rel="noopener noreferrer"
              className="qc-linkedin"
              data-testid="footer-linkedin"
              style={{ marginTop: 14 }}
            >
              {Icon.linkedin} Follow TMR Global on LinkedIn
            </a>
          </div>
          <div className="qc-footer-cols">
            <div>
              <h4>Platform</h4>
              <a href="#platform">QC4 overview</a>
              <a href="#modules">Modules</a>
              <a href="#differentiator">Frontline assurance</a>
              <a href="#outcomes">Outcomes</a>
            </div>
            <div>
              <h4>Learn</h4>
              <a href="#customers">Customers</a>
              <a href="#resources">Resources</a>
              <a href="#faq">FAQ</a>
            </div>
            <div>
              <h4>Legal</h4>
              <a href="/privacy" data-testid="footer-privacy">Privacy policy</a>
            </div>
            <div>
              <h4>Get in touch</h4>
              <a href="#demo">Book a demo</a>
              <a href="https://tidycal.com/dbenson/60-minute-meeting" target="_blank" rel="noopener noreferrer">TidyCal booking</a>
              <a href="mailto:daniel.benson@tmrglobal.com">daniel.benson@tmrglobal.com</a>
            </div>
          </div>
        </div>
        <div className="qc-copyright">
          <span>© 2026 TMR Global. All Rights Reserved.</span>
          <a
            href="https://www.linkedin.com/company/tmrglobal/?viewAsMember=true"
            target="_blank" rel="noopener noreferrer"
            className="qc-linkedin-btn"
            data-testid="footer-linkedin-bottom"
            aria-label="Follow TMR Global on LinkedIn"
          >
            {Icon.linkedin}
            <span>Follow us on LinkedIn</span>
          </a>
          <a href="/privacy" className="qc-copyright-link" data-testid="footer-privacy-inline">Privacy Policy</a>
        </div>
      </footer>
      {activeResource && (
        <ResourceModal resource={activeResource} onClose={() => setActiveResource(null)} />
      )}
    </div>
  )
}
